import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  DEFAULT_SECURITY_CONTEXT,
  detectBrowserWebAuthnCapability,
  ensureUserSecuritySettings,
  getAuthSecurityContext,
  logAuthSecurityEvent,
  updateUserSecuritySettings,
  type BrowserWebAuthnCapability,
  type SecurityContext,
} from "@/lib/auth/security";

interface AuthSecurityContextType {
  session: Session | null;
  user: User | null;
  loading: boolean;
  sessionRestored: boolean;
  error: string | null;
  webAuthnCapability: BrowserWebAuthnCapability;
  securityContext: SecurityContext | null;
  securityLoading: boolean;
  securityError: string | null;
  refreshSecurityContext: () => Promise<void>;
  updateSecuritySettings: (settings: {
    passkeyEnabled: boolean;
    biometricUnlockEnabled: boolean;
    rememberDevice: boolean;
  }) => Promise<boolean>;
  enablePasskey: () => Promise<boolean>;
  disablePasskey: () => Promise<boolean>;
  signInWithPasskey: () => Promise<{ ok: boolean; reason: string }>;
  signOutCleanly: () => Promise<void>;
}

const DEFAULT_WEB_AUTHN_CAPABILITY: BrowserWebAuthnCapability = {
  supported: false,
  platformAuthenticatorAvailable: false,
  reason: "WebAuthn capability has not been checked yet",
};

const AuthSecurityContext = createContext<AuthSecurityContextType | undefined>(undefined);

export function AuthSecurityProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionRestored, setSessionRestored] = useState(false);
  const [error] = useState<string | null>(null);

  const [webAuthnCapability, setWebAuthnCapability] = useState<BrowserWebAuthnCapability>(
    DEFAULT_WEB_AUTHN_CAPABILITY,
  );

  const [securityContext, setSecurityContext] = useState<SecurityContext | null>(null);
  const [securityLoading, setSecurityLoading] = useState(false);
  const [securityError, setSecurityError] = useState<string | null>(null);

  const hasLoggedSessionRestoreEvent = useRef(false);
  const hasInitializedSession = useRef(false);

  useEffect(() => {
    detectBrowserWebAuthnCapability().then(setWebAuthnCapability);
  }, []);

  const refreshSecurityContext = useCallback(async () => {
    if (!user) {
      setSecurityContext(null);
      setSecurityError(null);
      return;
    }

    setSecurityLoading(true);
    setSecurityError(null);

    try {
      await ensureUserSecuritySettings();
      const context = await getAuthSecurityContext();
      setSecurityContext(context);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unable to load account security settings";
      setSecurityError(message);
      setSecurityContext(DEFAULT_SECURITY_CONTEXT);
    } finally {
      setSecurityLoading(false);
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, nextSession) => {
      if (!mounted) return;

      setSession(nextSession);
      setUser(nextSession?.user ?? null);

      if (event === "INITIAL_SESSION") {
        hasInitializedSession.current = true;
        setSessionRestored(true);
        setLoading(false);

        if (nextSession?.user) {
          if (!hasLoggedSessionRestoreEvent.current) {
            hasLoggedSessionRestoreEvent.current = true;
            await logAuthSecurityEvent("session_restored", { method: "INITIAL_SESSION" });
          }
        }
        // Intentionally do not log "session_restore_failed" here when anonymous:
        // log_auth_security_event is authenticated-only, and the public login page
        // would generate a non-blocking warning. Backend telemetry already sees
        // the absence of a session.

        return;
      }

      if (event === "SIGNED_OUT") {
        setSecurityContext(null);
        setSecurityError(null);
        setSecurityLoading(false);
      }

      setLoading(false);
    });

    void supabase.auth.getSession().then(async ({ data, error: sessionError }) => {
      if (!mounted || hasInitializedSession.current) return;

      if (sessionError) {
        hasInitializedSession.current = true;
        setSession(null);
        setUser(null);
        setSessionRestored(true);
        setLoading(false);
        // sessionError before authentication cannot be logged to the
        // authenticated-only auth security event table; omit to avoid a
        // public-page warning. The user sees the failed restore via the null session.
        return;
      }

      setSession(data.session);
      setUser(data.session?.user ?? null);
      hasInitializedSession.current = true;
      setSessionRestored(true);
      setLoading(false);

      if (data.session?.user) {
        if (!hasLoggedSessionRestoreEvent.current) {
          hasLoggedSessionRestoreEvent.current = true;
          await logAuthSecurityEvent("session_restored", { method: "getSession_fallback" });
        }
      }
      // Anonymous getSession fallback is a normal unauthenticated state; do not
      // call the authenticated-only log_auth_security_event RPC here.
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setSecurityContext(null);
      setSecurityError(null);
      return;
    }

    refreshSecurityContext();
  }, [user, refreshSecurityContext]);

  const updateSecuritySettings = useCallback(async (settings: {
    passkeyEnabled: boolean;
    biometricUnlockEnabled: boolean;
    rememberDevice: boolean;
  }) => {
    if (!user) {
      setSecurityError("You need to be authenticated to update security settings");
      return false;
    }

    try {
      await updateUserSecuritySettings(settings);
      await refreshSecurityContext();
      return true;
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Unable to update security settings";
      setSecurityError(message);
      return false;
    }
  }, [refreshSecurityContext, user]);

  const enablePasskey = useCallback(async () => {
    const ok = await updateSecuritySettings({
      passkeyEnabled: true,
      biometricUnlockEnabled: !!securityContext?.biometricUnlockEnabled,
      rememberDevice: !!securityContext?.rememberDevice,
    });

    if (ok) {
      await logAuthSecurityEvent("passkey_enrolled", { source: "account_security_page" });
    }

    return ok;
  }, [securityContext?.biometricUnlockEnabled, securityContext?.rememberDevice, updateSecuritySettings]);

  const disablePasskey = useCallback(async () => {
    const ok = await updateSecuritySettings({
      passkeyEnabled: false,
      biometricUnlockEnabled: !!securityContext?.biometricUnlockEnabled,
      rememberDevice: !!securityContext?.rememberDevice,
    });

    if (ok) {
      await logAuthSecurityEvent("passkey_removed", { source: "account_security_page" });
    }

    return ok;
  }, [securityContext?.biometricUnlockEnabled, securityContext?.rememberDevice, updateSecuritySettings]);

  const signInWithPasskey = useCallback(async () => {
    const projectSupportsPasskeyFlow = !!securityContext?.passkeyProjectSupported;
    const browserSupportsPasskeyFlow =
      webAuthnCapability.supported && webAuthnCapability.platformAuthenticatorAvailable;

    if (!projectSupportsPasskeyFlow || !browserSupportsPasskeyFlow) {
      await logAuthSecurityEvent("passkey_login_failure", {
        reason: !projectSupportsPasskeyFlow
          ? "Passkey sign-in not enabled in Supabase project configuration"
          : "Passkey/WebAuthn not available in this browser",
      });

      return {
        ok: false,
        reason: !projectSupportsPasskeyFlow
          ? "Passkeys are not enabled for this Supabase project yet. Use email/password or magic link."
          : "Passkeys are not available in this browser/device. Use email/password or magic link.",
      };
    }

    // Supabase JS currently exposes WebAuthn in MFA APIs, not as universal first-factor passkey login.
    // Keep behavior honest and explicitly fallback until project-auth flow is configured end-to-end.
    await logAuthSecurityEvent("passkey_login_failure", {
      reason: "No first-factor passkey login flow configured for this project",
    });

    return {
      ok: false,
      reason: "Passkey sign-in is not fully configured for this project yet. Use email/password or magic link.",
    };
  }, [securityContext?.passkeyProjectSupported, webAuthnCapability.platformAuthenticatorAvailable, webAuthnCapability.supported]);

  const signOutCleanly = useCallback(async () => {
    await logAuthSecurityEvent("logout", { source: "client" });
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthSecurityContextType>(() => ({
    session,
    user,
    loading,
    sessionRestored,
    error,
    webAuthnCapability,
    securityContext,
    securityLoading,
    securityError,
    refreshSecurityContext,
    updateSecuritySettings,
    enablePasskey,
    disablePasskey,
    signInWithPasskey,
    signOutCleanly,
  }), [
    session,
    user,
    loading,
    sessionRestored,
    error,
    webAuthnCapability,
    securityContext,
    securityLoading,
    securityError,
    refreshSecurityContext,
    updateSecuritySettings,
    enablePasskey,
    disablePasskey,
    signInWithPasskey,
    signOutCleanly,
  ]);

  return <AuthSecurityContext.Provider value={value}>{children}</AuthSecurityContext.Provider>;
}

export function useAuthSecurity() {
  const context = useContext(AuthSecurityContext);
  if (!context) {
    throw new Error("useAuthSecurity must be used within AuthSecurityProvider");
  }

  return context;
}
